import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';

@Component({
    standalone: false,
    selector: 'ep-option-compare-contract-dialog',
    templateUrl: './object-compare-contract-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Default
})
export class ObjectCompareContractDialogComponent {
    model = {
        diffOnly: false,
        dataReady: false
    };

    dsContract: Datasource;
    dsContractSub: Subscription;

    optionID: any = [];
    contracts: any = [];
    itemData: any = [];
    lineDetails: any = [];

    protected parentActiveModal: NgbActiveModal;

    @Input()
    set dynamicData(value: any) {
        if (value && value.options) {
            this.optionID = value.options.split(',');
            this.getOptionData();
        }
    }

    constructor(public dataService: DataService, private activeModal: NgbActiveModal) {
        this.parentActiveModal = this.activeModal;
        this.dsContract = this.dataService.addNewDatasource('Contract');
    }

    ngOnInit() { }

    ngOnDestroy() {
        this.model.dataReady = false;
        this.model.diffOnly = false;
        this.unsubscribeFromContract();
        if (this.dsContract) {
            this.dataService.deleteDatasource(this.dsContract);
        }
    }

    async getOptionData() {
        for (var i in this.optionID) {
            await this.loadOption(this.optionID[i]);
        }
        this.buildData();
    }

    loadOption(id: any) {
        return new Promise(async (resolve) => {
            this.resetContractDS();
            this.dsContract.get({ Contract_ID: id, View: 'Summary', json: 'CT2' }, true, true);

            this.dsContractSub = this.dsContract.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.unsubscribeFromContract();
                        this.contracts.push(this.dsContract.objectNode);
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        this.unsubscribeFromContract();
                        resolve(false);
                        break;
                }
            });
        });
    }

    buildData() {
        this.addGeneralInfo();
        this.addLineInfo();
        this.addCoverageInfo();
        this.model.dataReady = true;
    }

    findTitleIndex(section: string): number {
        let index = -1;

        for (let i = 0; i < this.itemData.length; i++) {
            if (this.itemData[i].section == section && this.itemData[i].title == true) {
                index = i;
                break;
            }
        }

        return index;
    }

    findLOBSummaryIndex(lineCode: string): number {
        let index = -1;

        for (let i = 0; i < this.itemData.length; i++) {
            if (this.itemData[i].section == 'Summary' && this.itemData[i].lineCode == lineCode) {
                index = i;
                break;
            }
        }

        return index;
    }

    findLOBProductIndex(lineCode: string, productCode: string): number {
        let index = -1;

        for (let i = 0; i < this.itemData.length; i++) {
            if (this.itemData[i].section == lineCode && this.itemData[i].productCode == productCode) {
                index = i;
                break;
            }
        }

        return index;
    }

    addGeneralInfo() {
        this.addTitleItem('Contract', 'General Information');
        this.addGeneralItem('Contract', 'Option Description', 'Option_Desc');
        this.addGeneralItem('Contract', 'Contract Num', 'Contract_Num');
        this.addGeneralItem('Contract', 'Status', 'Option_Status_Desc');
        this.addGeneralItem('Contract', 'Market', 'Supplier.Entity_Name');
        this.addGeneralItem('Contract', 'LOB', 'Line_Code_Desc');
        this.addGeneralItem('Contract', 'Effective', 'Eff_Date', 'epDate:mm/dd/yy');
        this.addGeneralItem('Contract', 'Expiration', 'Exp_Date', 'epDate:mm/dd/yy');
        this.addGeneralItem('Contract', 'Total Premium', 'Full_Term_Prem', 'currency:USD:symbol:1.2-2');
        this.addGeneralItem('Contract', 'Total Fee', 'Full_Term_Fee', 'currency:USD:symbol:1.2-2');
        this.addGeneralItem('Contract', 'Total Tax', 'Full_Term_Tax', 'currency:USD:symbol:1.2-2');
    }

    addLineInfo() {
        let index = -1;
        let values = {};

        for (var c in this.contracts) {
            let contract = this.contracts[c];

            for (var lob in contract.Product_Summary.Contract_Line) {
                values = {};
                let line = contract.Product_Summary.Contract_Line[lob];
                index = this.findLOBSummaryIndex(line.Line_Code);

                if (index == -1) {
                    values = this.getLineTotals(line.Line_Code);
                    this.addLOBSummaryItem('Summary', line.Line_Code, line.Line_Code_Desc, values);
                }
            }
        }
    }

    getLineTotals(lineCode): any {
        let lineIdx = -1;
        let totalPrem = 0;
        let totalFee = 0;
        let totalTax = 0;

        let lobDiff = false;

        let premData: any = [];
        let feeData: any = [];
        let taxData: any = [];

        let lobSummaryData: any = {};

        for (var c in this.contracts) {
            totalPrem = 0;
            totalFee = 0;
            totalTax = 0;
            lineIdx = -1;

            let contract = this.contracts[c];

            for (let lob = 0; lob < contract.Product_Summary.Contract_Line.length; lob++) {
                if (contract.Product_Summary.Contract_Line[lob].Line_Code == lineCode) {
                    lineIdx = lob;
                    break;
                }
            }

            if (lineIdx > -1) {
                let line = contract.Product_Summary.Contract_Line[lineIdx];

                for (var p in line.Product) {
                    let product = line.Product[p];
                    let amount = product.Full_Term_Amount || 0;
                    let numAmount = Number(amount);

                    switch (product.Product_Cost_Type) {
                        case 'P':
                            totalPrem += numAmount;
                            break;
                        case 'F':
                            totalFee += numAmount;
                            break;
                        case 'T':
                            totalTax += numAmount;
                            break;
                    }
                }
                premData.push({ value: totalPrem.toFixed(2) });
                feeData.push({ value: totalFee.toFixed(2) });
                taxData.push({ value: totalTax.toFixed(2) });
            } else {
                lobDiff = true;
                premData.push({ value: 'n/a' });
                feeData.push({ value: 'n/a' });
                taxData.push({ value: 'n/a' });
            }
        }

        let firstValue;
        let different = false;

        firstValue = premData[0].value;
        for (let idx = 1; idx < premData.length; idx++) {
            if (premData[idx].value != firstValue) {
                different = true;
                break;
            }
        }
        lobSummaryData.totalPremValues = { hasDifference: different, values: premData, pipe: 'currency:USD:symbol:1.2-2' };

        different = false;

        firstValue = feeData[0].value;
        for (let idx = 1; idx < feeData.length; idx++) {
            if (feeData[idx].value != firstValue) {
                different = true;
                break;
            }
        }
        lobSummaryData.totalFeeValues = { hasDifference: different, values: feeData, pipe: 'currency:USD:symbol:1.2-2' };

        different = false;

        firstValue = taxData[0].value;
        for (let idx = 1; idx < taxData.length; idx++) {
            if (taxData[idx].value != firstValue) {
                different = true;
                break;
            }
        }
        lobSummaryData.totalTaxValues = { hasDifference: different, values: taxData, pipe: 'currency:USD:symbol:1.2-2' };
        lobSummaryData.hasDifference = lobDiff; //At least 1 contract doesn't include this LOB.

        return lobSummaryData;
    }

    addCoverageInfo() {
        let index = -1;
        let productData: any = {};
        let limitData = [];
        let deductData = [];
        let premData = [];

        for (var c in this.contracts) {
            let contract = this.contracts[c];

            for (var lob in contract.Product_Summary.Contract_Line) {
                let line = contract.Product_Summary.Contract_Line[lob];
                index = this.findTitleIndex(line.Line_Code);

                if (index == -1) {
                    this.addTitleItem(line.Line_Code, line.Line_Code_Desc + ' Coverage');
                }

                for (var p in line.Product) {
                    if (line.Product[p].Product_Cost_Type != 'P') {
                        continue;
                    }
                    //Check for index of Product and LineCode. If not found, add product info....
                    index = -1;
                    index = this.findLOBProductIndex(line.Line_Code, line.Product[p].Product_Code);
                    if (index == -1) {
                        productData = {};
                        limitData = [];
                        deductData = [];
                        premData = [];
                        for (var c2 in this.contracts) {
                            let product = this.getProductValue(c2, line.Line_Code, line.Product[p].Product_Code);
                            if (product && product != null && product.Product_Code) {
                                limitData.push({ value: product.Limit || '*' });
                                deductData.push({ value: product.Ded || '*' });
                                premData.push({ value: product.Full_Term_Amount || '*' });
                            } else {
                                limitData.push({ value: 'n/a' });
                                deductData.push({ value: 'n/a' });
                                premData.push({ value: 'n/a' });
                            }
                        }

                        let firstValue;
                        let different = false;

                        firstValue = limitData[0].value;
                        for (let idx = 1; idx < limitData.length; idx++) {
                            if (limitData[idx].value != firstValue) {
                                different = true;
                                break;
                            }
                        }
                        productData.limitValues = { hasDifference: different, values: limitData };

                        different = false;
                        firstValue = deductData[0].value;
                        for (let idx = 1; idx < deductData.length; idx++) {
                            if (deductData[idx].value != firstValue) {
                                different = true;
                                break;
                            }
                        }
                        productData.deductValues = { hasDifference: different, values: deductData };

                        different = false;
                        firstValue = premData[0].value;
                        for (let idx = 1; idx < premData.length; idx++) {
                            if (premData[idx].value != firstValue) {
                                different = true;
                                break;
                            }
                        }
                        productData.premValues = { hasDifference: different, values: premData, pipe: 'currency:USD:symbol:1.2-2' };

                        this.addProductItem(
                            line.Line_Code,
                            line.Product[p].Product_Code,
                            line.Product[p].Product_Code_Desc,
                            productData
                        );
                    }
                }
            }
        }
    }
    addTitleItem(section: string, label: string) {
        this.itemData.push({ section: section, label: label, title: true });
    }

    addGeneralItem(section: string, label: string, fieldPath?: any, pipe?: any) {
        let data: any = [];
        let firstValue;
        let different = false;
        let value;
        if (!pipe || pipe == '') {
            pipe = 'none';
        }

        if (section == 'Contract') {
            for (var i in this.contracts) {
                value = this.getDataValue(i, fieldPath);
                data.push({ value: value });
            }

            firstValue = data[0].value;
            for (let idx = 1; idx < data.length; idx++) {
                if (data[idx].value != firstValue) {
                    different = true;
                    break;
                }
            }

            this.itemData.push({
                section: section,
                label: label,
                title: false,
                values: data,
                pipe: pipe,
                hasDifference: different
            });
        }
    }

    addLOBSummaryItem(section: string, lineCode: string, label: string, data: any) {
        //Only add the section title once.
        let index = -1;
        index = this.findTitleIndex('Summary');
        if (index == -1) {
            this.addTitleItem('Summary', 'Summary by LOB');
        }

        this.itemData.push({
            section: section,
            label: label,
            lineCode: lineCode,
            title: false,
            values: data
        });
    }

    addProductItem(section: string, productCode: string, label: string, data: any) {
        //Find index of section (LOB) title and add right after

        let idx = this.findTitleIndex(section);
        idx++;

        this.itemData.splice(idx, 0, {
            section: section,
            label: label,
            productCode: productCode,
            title: false,
            values: data
        });
    }

    getProductValue(contractIdx: any, lineCode: string, productCode: string): any {
        let product: any = null;

        if (this.contracts[contractIdx]) {
            let contract = this.contracts[contractIdx];
            lineLoop: for (var l in contract.Product_Summary.Contract_Line) {
                let line = contract.Product_Summary.Contract_Line[l];
                if (line.Line_Code == lineCode) {
                    for (var p in line.Product) {
                        if (line.Product[p].Product_Code == productCode) {
                            product = line.Product[p];
                            break lineLoop;
                        }
                    }
                }
            }
        }

        return product;
    }

    getDataValue(contractIDX: any, path: string) {
        let segment: string[] = path.split('.');
        let value = this.contracts[contractIDX];

        for (let i = 0; i < segment.length; i++) {
            value = value[segment[i]];
            if (value === 'undefined') {
                return '';
            }
            if (value == null) {
                return '';
            }
        }
        return value;
    }

    resetContractDS() {
        this.unsubscribeFromContract();
        if (this.dsContract) {
            this.dsContract.reset();
        }
    }

    unsubscribeFromContract() {
        if (this.dsContractSub) {
            this.dsContractSub.unsubscribe();
        }
    }

    closeModal(returnValue: any) {
        if (this.parentActiveModal) {
            this.parentActiveModal.close(returnValue);
            return;
        } else {
            throw 'Dialog did not properly set parentActiveModal in "initialize()"';
        }
    }

    close() {
        this.closeModal({ action: 'Close' });
    }
}
